import React, { useState, useEffect } from 'react';
import { Loader2, X, Edit2, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useFrame } from '../contexts/FrameContext';
import frameOrderConfig from '../config/frameOrderConfig.json';
import { optimizeImageForFrame } from '../lib/frameImageOptimization';
import { updatePropertySearchUpscaledImage, parseAddressComponents } from '../services/imageProcessing';
import { PropertySearch } from '../types';
import { createFrameCheckoutSession } from '../lib/stripe';
import ShippingCalculator from './ShippingCalculator';
import { notifyCheckoutAccess, notifyStripeAccess } from '../utils/slackNotifications';
import { propertyTokenGenerator } from '../services/tokenGenerator';
import { campaignTracking } from '../services/campaignTracking';
import { 
  trackOrderSummaryView, 
  trackCheckoutButtonClick, 
  trackStripeCheckoutView, 
  trackFunnelStep,
  trackError,
  trackEventWithUserId,
  trackFunnelStepWithUserId,
  getStoredUserId,
  trackUserMilestone,
  trackRedditOrderStart,
  trackRedditPurchase,
  trackFacebookOrderStart,
  trackPinterestOrderStart
} from '../utils/analytics';
import { updateWidgetForOrderForm } from '../utils/widgetSync';

interface OrderFormProps {
  onClose: () => void;
}

interface AddressFields {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

const OrderForm: React.FC<OrderFormProps> = ({ onClose }) => {
  console.log('🛒 OrderForm opened');
  
  const [checkoutStep, setCheckoutStep] = useState<'details' | 'shipping'>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [useSameForBilling, setUseSameForBilling] = useState(true);
  const [showBillingFields, setShowBillingFields] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isLoadingPropertyData, setIsLoadingPropertyData] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [shippingMethod, setShippingMethod] = useState<'ground' | 'overnight'>('ground');
  const [shippingCost, setShippingCost] = useState<number | null>(null); // No default cost until calculated
  const [lumaShippingMethod, setLumaShippingMethod] = useState<string>('ground'); // Store the actual Lumaprints method
  const [isAddressConfirmed, setIsAddressConfirmed] = useState(false);
  const [pendingAddressChanges, setPendingAddressChanges] = useState(false);
  const [shippingCalculated, setShippingCalculated] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  
  // Log email state changes for debugging
  useEffect(() => {
    console.log('📧 Email state changed to:', email);
  }, [email]);
  
  const [shippingAddress, setShippingAddress] = useState<AddressFields>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US' // Always default to US
  });
  
  const [billingAddress, setBillingAddress] = useState<AddressFields>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US' // Always default to US
  });

  const { 
    selectedFrame, 
    selectedStyle, 
    selectedSize, 
    address, 
    propertySearchId, 
    propertyImage, 
    styleImages,
    imageSource
  } = useFrame();
  
  console.log('📦 OrderForm context data:', {
    selectedFrame,
    selectedStyle,
    selectedSize,
    address,
    propertySearchId,
    propertyImage: propertyImage ? 'exists' : 'null',
    styleImages: styleImages ? Object.keys(styleImages).filter(k => styleImages[k as keyof typeof styleImages]) : [],
    imageSource
  });

  // Get the image URL for the selected style
  const getImageUrl = () => {
    if (selectedStyle === 'pencil' && styleImages?.pencil) {
      return styleImages.pencil;
    } else if (selectedStyle === 'oil' && styleImages?.oil) {
      return styleImages.oil;
    } else if (selectedStyle === 'charcoal' && styleImages?.charcoal) {
      return styleImages.charcoal;
    } else if (selectedStyle === 'chalk' && styleImages?.chalk) {
      return styleImages.chalk;
    } else if (propertyImage) {
      return propertyImage;
    }
    return '';
  };

  // Get the frame URL for display
  const getFrameUrl = (frameId: string) => {
    // Capitalize first letter for template naming convention (e.g., "black" -> "Black")
    const frameName = frameId.charAt(0).toUpperCase() + frameId.slice(1);
    // Use Medium size for preview (templates are named like Black_M.png)
    const url = `https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates/${frameName}_M.png`;
    console.log('🖼️ Getting frame URL for:', frameId, '→', url);
    return url;
  };

  // Track if initial widget update has been sent
  const [initialWidgetUpdateSent, setInitialWidgetUpdateSent] = useState(false);

  // Fetch and prefill email from database or localStorage on mount
  useEffect(() => {
    const fetchCustomerEmail = async () => {
      console.log('📧 Attempting to fetch customer email...');
      console.log('🔍 propertySearchId:', propertySearchId);
      
      // Try database first if we have a propertySearchId
      if (propertySearchId) {
        try {
          console.log('🔍 Querying database for email...');
          const { data, error } = await supabase
            .from('property_searches')
            .select('customer_email')
            .eq('id', propertySearchId)
            .maybeSingle();
          
          console.log('🔍 Database query result:', { data, error });
          
          if (!error && data?.customer_email) {
            console.log('✅ Email retrieved from database:', data.customer_email);
            setEmail(data.customer_email);
            console.log('✅ Email state updated');
            return;
          } else {
            console.log('ℹ️ No email found in database');
          }
        } catch (error) {
          console.error('❌ Error fetching email from database:', error);
        }
      } else {
        console.log('ℹ️ No propertySearchId available, skipping database lookup');
      }
      
      // Fallback to localStorage
      console.log('🔍 Checking localStorage for email...');
      const storedEmail = localStorage.getItem('customer_email');
      if (storedEmail) {
        console.log('✅ Email retrieved from localStorage:', storedEmail);
        setEmail(storedEmail);
        console.log('✅ Email state updated');
      } else {
        console.log('ℹ️ No stored email found in localStorage');
      }
    };
    
    fetchCustomerEmail();
  }, [propertySearchId]); // Re-run if propertySearchId changes

  // Send initial widget update when checkout opens (with image URLs, no user info yet)
  useEffect(() => {
    if (initialWidgetUpdateSent) return; // Prevent duplicate sends
    
    console.log('🚀 Sending initial widget update on checkout open');
    const artworkUrl = getImageUrl();
    const frameUrl = getFrameUrl(selectedFrame);
    
    console.log('🖼️ Initial image URLs:', {
      artworkUrl,
      frameUrl,
      selectedFrame,
      selectedStyle,
      selectedSize
    });
    
    updateWidgetForOrderForm(
      '', // No firstName yet
      '', // No lastName yet
      '', // No email yet
      address || '',
      artworkUrl,
      frameUrl,
      selectedFrame,
      selectedStyle,
      selectedSize,
      productPrice * 0.80, // Discounted product price
      shippingCost || 0,
      discountedTotal
    );
    
    setInitialWidgetUpdateSent(true);
  }, []); // Run once on mount

  // Function to parse address and extract street number + name only
  const parseStreetAddress = (fullAddress: string): string => {
    try {
      // Remove common country suffixes
      const cleanAddress = fullAddress.replace(/,?\s*(USA|US|United States)$/i, '').trim();
      
      // Split by commas and get the first part (street address)
      const parts = cleanAddress.split(',').map(part => part.trim());
      
      // Return only the first part which should be the street number and name
      return parts[0] || fullAddress;
    } catch (error) {
      console.error('Error parsing street address:', error);
      return fullAddress;
    }
  };

  // Function to parse full address into components
  const parseFullAddress = (fullAddress: string): {
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  } => {
    try {
      // Remove common country suffixes
      const cleanAddress = fullAddress.replace(/,?\s*(USA|US|United States)$/i, '').trim();
      
      // Split by commas and trim each part
      const parts = cleanAddress.split(',').map(part => part.trim());
      
      const result: {
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
      } = {};
      
      if (parts.length >= 2) {
        // Second part is usually the city
        result.city = parts[1];
      }
      
      if (parts.length >= 3) {
        // Third part usually contains state and zip code
        const stateZipPart = parts[2];
        const stateZipMatch = stateZipPart.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
        
        if (stateZipMatch) {
          result.state = stateZipMatch[1];
          result.zipCode = stateZipMatch[2];
        } else {
          // If it doesn't match the pattern, try to extract state and zip separately
          const stateMatch = stateZipPart.match(/([A-Za-z]{2})/);
          const zipMatch = stateZipPart.match(/(\d{5}(?:-\d{4})?)/);
          
          if (stateMatch) result.state = stateMatch[1];
          if (zipMatch) result.zipCode = zipMatch[1];
        }
      }
      
      // Default country to US if not specified
      result.country = 'US';
      
      return result;
    } catch (error) {
      console.error('Error parsing full address:', error);
      return {};
    }
  };

  // Check if address is complete and valid
  const isAddressComplete = (address: AddressFields): boolean => {
    return !!(
      address.addressLine1.trim() &&
      address.city.trim() &&
      address.state.trim() &&
      address.zipCode.trim()
    );
  };

  // Reset address confirmation when address changes
  useEffect(() => {
    if (isAddressComplete(shippingAddress)) {
      // If we were loading property data and now have a complete address, mark as confirmed
      if (isLoadingPropertyData) {
        return; // Don't reset confirmation while loading
      }
      
      // If address was complete from the start (loaded from database), auto-confirm
      if (!pendingAddressChanges) {
        setIsAddressConfirmed(true);
      } else {
        // User has made changes, require confirmation
        setIsAddressConfirmed(false);
      }
    } else {
      // Incomplete address, not confirmed
      setIsAddressConfirmed(false);
    }
  }, [shippingAddress, isLoadingPropertyData, pendingAddressChanges]);
  
  // Send checkout notification and track order summary access when component mounts
  useEffect(() => {
    const sendNotificationsAndTrack = async () => {
      try {
        // Send checkout notification
        await notifyCheckoutAccess(
          imageSource || 'address',
          selectedStyle || 'watercolor', 
          selectedFrame || 'black', 
          (selectedSize || 'M') as 'XS' | 'S' | 'M' | 'L',
          address || 'Unknown address'
        );
        
        // Track order summary accessed
        if (propertySearchId) {
          const { campaignTracking } = await import('../services/campaignTracking');
          const { propertyTokenGenerator } = await import('../services/tokenGenerator');
          
          // Use bridge function to find property in both tables
          let campaignInfo = null;
          try {
            // First try to get property data to find access code
            const { data: propertyData } = await supabase
              .from('property_searches')
              .select('property_token, address')
              .eq('id', propertySearchId)
              .maybeSingle();
            
            if (propertyData?.property_token) {
              // Use bridge function to get campaign info
              const propertyResult = await propertyTokenGenerator.bridgeAccessCodeToPropertySearch(propertyData.property_token);
              if (propertyResult) {
                campaignInfo = await campaignTracking.getCampaignPropertyInfo(propertyResult.accessCode || propertyData.property_token);
              }
            }
          } catch (bridgeError) {
            console.log('Could not find campaign info for property:', propertySearchId);
          }
          
          await campaignTracking.trackOrderSummaryAccessed({
            propertyId: propertySearchId,
            campaignId: campaignInfo?.campaignId,
            accessCode: campaignInfo?.accessCode
          });
          
          console.log('✅ Tracked order summary access for property:', propertySearchId, {
            campaignId: campaignInfo?.campaignId,
            accessCode: campaignInfo?.accessCode,
            isCampaignProperty: !!campaignInfo?.campaignId
          });
        }
      } catch (error) {
        console.error('Error sending notifications or tracking:', error);
      }
    };
    
    sendNotificationsAndTrack();
  }, [selectedStyle, selectedFrame, selectedSize, address, propertySearchId, imageSource]);
  
  // Fetch property search data when component mounts or propertySearchId changes
  useEffect(() => {
    const fetchPropertySearchData = async () => {
      if (!propertySearchId) {
        console.log('No property search ID available');
        return;
      }

      setIsLoadingPropertyData(true);
      
      try {
        console.log('Fetching property search data for ID:', propertySearchId);
        
        // Ensure client session ID is set in headers before making the request
        const { refreshClientSessionId } = await import('../lib/supabase');
        refreshClientSessionId();
        
        const { data, error } = await supabase
          .from('property_searches')
          .select(`
            customer_first_name,
            customer_last_name,
            customer_email,
            address_line_1,
            address_line_2,
            city,
            state,
            zip_code,
            country
          `)
          .eq('id', propertySearchId)
          .maybeSingle<PropertySearch>();  // Use maybeSingle() to handle 0 or 1 row

        if (error) {
          console.error('Error fetching property search:', error);
          console.warn('Database access failed, likely due to RLS policies. User will need to enter address manually.');
          setError('Unable to load saved address information. Please enter your address manually.');
          return; // Don't throw, just return and let user enter address manually
        }
        
        if (!data) {
          console.warn('No property search data found for ID:', propertySearchId);
          // Don't show error, just let user enter address manually
          return;
        }

        if (data) {
          console.log('Retrieved property search data:', data);
          console.log('Address data from database:', {
            address_line_1: data.address_line_1,
            address_line_2: data.address_line_2,
            city: data.city,
            state: data.state,
            zip_code: data.zip_code,
            country: data.country
          });
          
          // Pre-populate email if available from database
          // If not in database, preserve any email already set (e.g., from localStorage)
          if (data.customer_email) {
            console.log('✅ Pre-populating email from database:', data.customer_email);
            setEmail(data.customer_email);
          } else {
            console.log('ℹ️ No email in database, keeping current email state (may have been loaded from localStorage)');
            // Don't clear the email - it might have been set from localStorage
          }
          
          // Keep first and last name empty - always start fresh
          setFirstName('');
          setLastName('');
          
          // Build shipping address from database fields
          const shippingAddressData: AddressFields = {
            addressLine1: data.address_line_1 || '',
            addressLine2: data.address_line_2 || '',
            city: data.city || '',
            state: data.state || '',
            zipCode: data.zip_code || '', // Convert null to empty string
            country: 'US' // Always default to US, no option to change
          };
          
          // Debug logging for ZIP code specifically
          console.log('Shipping address being set:', shippingAddressData);
          
          // Set the shipping address without triggering confirmation requirement
          setShippingAddress(shippingAddressData);
          setPendingAddressChanges(false); // Address loaded from database, not user changes
          
          // Also update billing address if using same for billing
          if (useSameForBilling) {
            setBillingAddress(shippingAddressData);
          }
          
          // Clear any previous errors if data loaded successfully
          setError(null);
        }
      } catch (error) {
        console.error('Failed to fetch property search data:', error);
        console.warn('Database access completely failed. User will need to enter address manually.');
        // Don't show a scary error message, just a helpful one
        setError('Please enter your shipping address below.');
      } finally {
        setIsLoadingPropertyData(false);
      }
    };

    fetchPropertySearchData();
  }, [propertySearchId, useSameForBilling]);

  // Helper function to check if a field is invalid (empty and user has attempted submit)
  const isFieldInvalid = (value: string, required: boolean = true) => {
    return required && hasAttemptedSubmit && !value.trim();
  };

  // Get the selected size details
  const selectedSizeDetails = frameOrderConfig.frameSizes.find(
    size => size.id === selectedSize
  );

  // Get the selected frame details
  const selectedFrameDetails = frameOrderConfig.frameTypes.find(
    frame => frame.id === selectedFrame
  );

  // Calculate total price
  const productPrice = selectedSizeDetails?.price || 0;
  const totalPrice = productPrice + (shippingCost || 0);
  const discountedTotal = (productPrice * 0.80) + (shippingCost || 0); // Discount only on product, not shipping

  // Track order summary view when prices are calculated
  useEffect(() => {
    if (productPrice > 0 && shippingCost !== null) {
      console.log('💰 Order form prices calculated:', {
        productPrice,
        discountedTotal,
        shippingCost
      });
      
      trackOrderSummaryView({
        total: discountedTotal,
        subtotal: productPrice * 0.80, // Discounted product price
        shipping: shippingCost,
        style: selectedStyle,
        frame: selectedFrame,
        size: selectedSize,
        productId: `frame_${selectedStyle}_${selectedFrame}_${selectedSize}`
      });
      
      trackFunnelStep('Order Summary Viewed', 6, {
        order_value: discountedTotal,
        style: selectedStyle,
        frame: selectedFrame,
        size: selectedSize
      });

      // Note: Widget updates are handled separately to avoid duplicates:
      // 1. Initial update on mount (with images, empty user info)
      // 2. Update on address confirmation (with full user info + address)
    }
  }, [productPrice, discountedTotal, shippingCost, selectedStyle, selectedFrame, selectedSize]);

  // Get mat size based on selected frame size
  const getMatSize = (size: string) => {
    const matSizeMap = {
      'XS': '1.5"',
      'S': '2.5"',
      'M': '3"', 
      'L': '3.5"'
    };
    return matSizeMap[size as keyof typeof matSizeMap] || '3"';
  };

  // Get display label for size (user-facing label)
  const getSizeDisplayLabel = (size: string) => {
    const displayMap = {
      'XS': 'S',   // XS → S for users
      'S': 'M',    // S → M for users
      'M': 'L',    // M → L for users
      'L': 'XL'    // L → XL for users
    };
    return displayMap[size as keyof typeof displayMap] || size;
  };

  const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mark that user has made changes
    setPendingAddressChanges(true);
    
    // Reset shipping calculation when address changes
    setShippingCalculated(false);
    
    // If using shipping as billing, update billing address too
    if (useSameForBilling) {
      setBillingAddress(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleBillingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBillingAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleUseSameForBillingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setUseSameForBilling(checked);
    
    if (checked) {
      // Copy shipping address to billing address
      setBillingAddress({...shippingAddress});
      setShowBillingFields(false);
    } else {
      setShowBillingFields(true);
    }
  };

  // Handle shipping method and cost updates from ShippingCalculator
  const handleShippingUpdate = (method: 'ground' | 'overnight', cost: number, lumaMethod?: string) => {
    console.log('=== SHIPPING UPDATE RECEIVED IN ORDER FORM ===');
    console.log('Method:', method);
    console.log('Cost:', cost);
    console.log('Luma Method:', lumaMethod);
    console.log('Setting shipping method and cost...');
    
    setShippingMethod(method);
    setShippingCost(cost);
    setLumaShippingMethod(lumaMethod || method); // Use lumaMethod if available, otherwise fallback to method
    setShippingCalculated(true);
    
    console.log('Shipping cost updated in OrderForm state:', cost);
    console.log('Luma shipping method updated:', lumaMethod || method);
    console.log('New total price will be:', (productPrice + cost).toFixed(2));
  };

  // Confirm address function
  const handleConfirmAddress = async () => {
    if (!isAddressComplete(shippingAddress)) return;
    
    try {
      console.log('=== STARTING ADDRESS CONFIRMATION ===');
      console.log('Valid address entered:', shippingAddress);
      console.log('Customer details:', { email, firstName, lastName });
      console.log('Property search ID:', propertySearchId);

      // Parse the address components from the existing AddressFields structure
      const parsedComponents = {
        address_line_1: shippingAddress.addressLine1?.trim(),
        address_line_2: shippingAddress.addressLine2?.trim() || '',
        city: shippingAddress.city?.trim(),
        state: shippingAddress.state?.trim(),
        zip_code: shippingAddress.zipCode?.trim(),
        country: shippingAddress.country || 'United States'
      };
      
      console.log('Using existing address components:', parsedComponents);

      if (!parsedComponents.address_line_1 || !parsedComponents.zip_code) {
        throw new Error('Address line 1 and zip code are required');
      }

      // Save customer data first
      const customerUpdateData = {
        customer_first_name: firstName.trim(),
        customer_last_name: lastName.trim(),
        customer_email: email.trim(),
        address_line_1: parsedComponents.address_line_1,
        address_line_2: parsedComponents.address_line_2,
        city: parsedComponents.city,
        state: parsedComponents.state,
        zip_code: parsedComponents.zip_code,
        country: parsedComponents.country || 'United States',
        updated_at: new Date().toISOString()
      };

      console.log('Saving customer data to database:', customerUpdateData);

      const { error: updateError } = await supabase
        .from('property_searches')
        .update(customerUpdateData)
        .eq('id', propertySearchId);

      if (updateError) {
        console.error('Error updating property search with customer data:', updateError);
        throw updateError;
      }

      console.log('✅ Customer data saved successfully');

      // DISABLED: Automatic token conversion from temporary to address-based
      // Keep temporary tokens for webcam/image uploads to maintain generic links
      // Only create address-based tokens when explicitly needed during share functionality
      /*
      // Update token from temporary to address-based if we have address components
      if (parsedComponents.address_line_1 && parsedComponents.zip_code && shippingAddress.zipCode && propertySearchId) {
        console.log('=== UPDATING TOKEN FROM TEMPORARY TO ADDRESS-BASED ===');
        const fullAddress = `${parsedComponents.address_line_1} ${parsedComponents.city} ${parsedComponents.state}`;
        
        const tokenUpdate = await propertyTokenGenerator.updateToAddressToken(
          propertySearchId, // Now guaranteed to be non-null
          fullAddress, 
          shippingAddress.zipCode // Use the original field which is definitely a string
        );

        if (tokenUpdate.success) {
          console.log('✅ Token updated successfully:', {
            oldToken: tokenUpdate.oldToken,
            newToken: tokenUpdate.newToken
          });
          
          // Generate shareable URL with new token
          if (tokenUpdate.newToken) {
            const directUrl = propertyTokenGenerator.generateDirectUrl(tokenUpdate.newToken);
            console.log('📎 Shareable URL:', directUrl);
          }
        } else {
          console.warn('⚠️ Failed to update token, but continuing with order');
        }
      }
      */

      setIsAddressConfirmed(true);
      setPendingAddressChanges(false);
      setIsEditingAddress(false);
      
      console.log('=== ADDRESS CONFIRMATION COMPLETED ===');
      
      // Build full address string
      const fullAddress = [
        shippingAddress.addressLine1,
        shippingAddress.addressLine2,
        shippingAddress.city,
        shippingAddress.state,
        shippingAddress.zipCode
      ].filter(Boolean).join(', ');
      
      // Track address confirmation with actual customer data
      window.trackInteraction?.('address_confirmed', {
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || '',
        address: fullAddress,
        has_name: !!(firstName && lastName),
        has_email: !!email,
        address_source: propertySearchId ? 'property_search' : 'manual_entry'
      });
      
      // Update widget with confirmed address + user info (if available)
      
      console.log('📍 Updating widget with confirmed address:', {
        firstName,
        lastName,
        email,
        fullAddress,
        hasUserInfo: !!(firstName && lastName && email)
      });
      
      const artworkUrl = getImageUrl();
      const frameUrl = getFrameUrl(selectedFrame);
      
      updateWidgetForOrderForm(
        firstName || '',
        lastName || '',
        email || '',
        fullAddress,
        artworkUrl,
        frameUrl,
        selectedFrame,
        selectedStyle,
        selectedSize,
        productPrice * 0.80, // Discounted product price
        shippingCost || 0,
        discountedTotal
      );
    } catch (error) {
      console.error('Error in handleConfirmAddress:', error);
      alert('Failed to confirm address. Please try again.');
    }
  };

  // Save frame configuration to database
  const saveFrameConfigToDatabase = async () => {
    if (!propertySearchId) {
      console.warn('No property search ID available for saving frame config');
      return;
    }

    try {
      console.log('=== SAVING FRAME CONFIG TO DATABASE ===');
      
      const imageUrl = getImageUrl();
      
      // Validate that we have complete address data before saving
      const addressValidation = {
        hasFirstName: !!firstName.trim(),
        hasLastName: !!lastName.trim(),
        hasEmail: !!email.trim(),
        hasAddressLine1: !!shippingAddress.addressLine1.trim(),
        hasCity: !!shippingAddress.city.trim(),
        hasState: !!shippingAddress.state.trim(),
        hasZipCode: !!shippingAddress.zipCode.trim()
      };
      
      console.log('Address validation:', addressValidation);
      
      if (!addressValidation.hasFirstName || !addressValidation.hasLastName || !addressValidation.hasEmail) {
        console.error('❌ Missing required customer information');
        return false;
      }
      
      if (!addressValidation.hasAddressLine1 || !addressValidation.hasCity || !addressValidation.hasState || !addressValidation.hasZipCode) {
        console.error('❌ Missing required address information');
        return false;
      }
      
      const updateData = {
        selected_frame: selectedFrame,
        selected_size: selectedSize,
        selected_style: selectedStyle,
        frame_image_url: imageUrl,
        frame_price: productPrice,
        shipping_method: shippingMethod,
        shipping_cost: shippingCost,
        luma_shipping_method: lumaShippingMethod,
        total_price: discountedTotal, // Save the actual amount customer will pay
        customer_first_name: firstName.trim(),
        customer_last_name: lastName.trim(),
        customer_email: email.trim(),
        address_line_1: shippingAddress.addressLine1.trim(),
        address_line_2: shippingAddress.addressLine2?.trim() || '',
        city: shippingAddress.city.trim(),
        state: shippingAddress.state.trim(),
        zip_code: shippingAddress.zipCode.trim(),
        country: shippingAddress.country,
        checkout_initiated_at: new Date().toISOString(),
        order_status: 'checkout_initiated',
        updated_at: new Date().toISOString()
      };
      
      console.log('Frame config being saved to database:', {
        propertySearchId,
        selectedFrame,
        selectedSize,
        selectedStyle,
        imageUrl,
        productPrice,
        shippingMethod,
        shippingCost,
        totalPrice,
        customerInfo: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim()
        },
        addressInfo: {
          addressLine1: shippingAddress.addressLine1.trim(),
          addressLine2: shippingAddress.addressLine2?.trim() || '',
          city: shippingAddress.city.trim(),
          state: shippingAddress.state.trim(),
          zipCode: shippingAddress.zipCode.trim(),
          country: shippingAddress.country
        }
      });

      const { data, error } = await supabase
        .from('property_searches')
        .update(updateData)
        .eq('id', propertySearchId)
        .select('id, customer_first_name, customer_last_name, customer_email, address_line_1, address_line_2, city, state, zip_code, country');

      if (error) {
        console.error('❌ Error saving frame config to database:', error);
        console.error('Error details:', error.details, error.hint, error.code);
        throw error;
      }

      console.log('✅ Frame configuration saved to database successfully');
      console.log('Saved data verification:', data);
      
      // Verify that the address was saved correctly
      if (data && data[0]) {
        const saved = data[0];
        const addressMatch = {
          firstName: saved.customer_first_name === firstName.trim(),
          lastName: saved.customer_last_name === lastName.trim(),
          email: saved.customer_email === email.trim(),
          addressLine1: saved.address_line_1 === shippingAddress.addressLine1.trim(),
          city: saved.city === shippingAddress.city.trim(),
          state: saved.state === shippingAddress.state.trim(),
          zipCode: saved.zip_code === shippingAddress.zipCode.trim(),
          country: saved.country === shippingAddress.country
        };
        
        console.log('✅ Address verification:', addressMatch);
        
        // Check if any fields don't match
        const mismatches = Object.entries(addressMatch).filter(([key, match]) => !match);
        if (mismatches.length > 0) {
          console.error('❌ Address verification failed for fields:', mismatches.map(([key]) => key));
        } else {
          console.log('✅ All address fields verified successfully');
        }
      }
      
      return true;
    } catch (error) {
      console.error('❌ Failed to save frame config to database:', error);
      return false;
    }
  };

  // Handle step 1: collect customer details
  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate required fields
    if (!firstName.trim()) {
      setError('Please enter your first name');
      return;
    }
    
    if (!lastName.trim()) {
      setError('Please enter your last name');
      return;
    }
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    // Update widget with customer details and product info
    console.log('📝 Submitting customer details to widget');
    const artworkUrl = getImageUrl();
    const frameUrl = getFrameUrl(selectedFrame);
    
    console.log('📤 Sending details:', {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      artworkUrl,
      frameUrl,
      selectedFrame,
      selectedStyle,
      selectedSize
    });
    
    updateWidgetForOrderForm(
      firstName.trim(),
      lastName.trim(),
      email.trim(),
      address || '',
      artworkUrl,
      frameUrl,
      selectedFrame,
      selectedStyle,
      selectedSize,
      productPrice * 0.80, // Discounted product price
      shippingCost || 0,
      discountedTotal
    );
    
    // Move to shipping step
    setCheckoutStep('shipping');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields (already validated in step 1, but double-check)
      if (!firstName.trim()) {
        throw new Error('Please enter your first name');
      }
      
      if (!lastName.trim()) {
        throw new Error('Please enter your last name');
      }
      
      if (!email.trim()) {
        throw new Error('Please enter your email address');
      }

      // Validate shipping address required fields
      if (!shippingAddress.addressLine1.trim()) {
        throw new Error('Please enter your street address');
      }

      if (!shippingAddress.city.trim()) {
        throw new Error('Please enter your city');
      }

      if (!shippingAddress.state.trim()) {
        throw new Error('Please enter your state');
      }
      
      // Validate shipping address including ZIP code
      if (!shippingAddress.zipCode.trim()) {
        throw new Error('Please enter a valid ZIP code');
      }

      // Validate address is confirmed
      if (!isAddressConfirmed) {
        throw new Error('Please confirm your shipping address');
      }

      // Validate shipping has been calculated
      if (!shippingCalculated || shippingCost === null) {
        throw new Error('Please wait for shipping costs to be calculated');
      }
      
      console.log('=== ORDER SUBMISSION DEBUG ===');
      console.log('Order submission - ZIP code being sent:', shippingAddress.zipCode);
      console.log('Shipping method:', shippingMethod);
      console.log('Shipping cost being passed to Stripe:', shippingCost);
      console.log('Product price:', productPrice);
      console.log('Total price:', totalPrice);
      
      // Get the image URL
      let imageUrl = getImageUrl();
      if (!imageUrl) {
        throw new Error('No image available for order');
      }

      // --- Frame-specific image optimization ---
      console.log('Starting frame-specific image optimization for size:', selectedSize);
      setIsOptimizing(true);
      
      let optimizedImageUrl: string | null = null;
      
      try {
        // Optimize image for the specific frame size
        optimizedImageUrl = await optimizeImageForFrame(imageUrl, selectedSize as 'XS' | 'S' | 'M' | 'L');
        
        // Update the optimized image URL in the database
        if (propertySearchId && optimizedImageUrl) {
          await updatePropertySearchUpscaledImage(propertySearchId, optimizedImageUrl);
          console.log('Saved optimized image URL to database:', optimizedImageUrl);
        }
        
        // Use the optimized image for the order
        imageUrl = optimizedImageUrl;
        console.log('Image optimized successfully for frame size:', selectedSize);
      } catch (optimizeError) {
        console.warn('Frame optimization failed:', optimizeError);
        console.log('Continuing with original image');
        // Continue with original image if optimization fails
      } finally {
        setIsOptimizing(false);
      }
      // --- End optimization ---

      // CRITICAL: Save frame configuration to database before checkout
      console.log('=== SAVING FRAME CONFIG TO DATABASE BEFORE CHECKOUT ===');
      const frameConfigSaved = await saveFrameConfigToDatabase();
      
      if (!frameConfigSaved) {
      console.warn('Failed to save frame config to database, but continuing with checkout');
    }

      // BACKUP: Also store frame configuration in localStorage as fallback
      console.log('=== STORING FRAME CONFIG IN LOCALSTORAGE AS BACKUP ===');
      console.log('selectedFrame:', selectedFrame);
      console.log('selectedSize:', selectedSize);
      console.log('selectedStyle:', selectedStyle);
      console.log('imageUrl:', imageUrl);
      console.log('selectedSizeDetails:', selectedSizeDetails);
      
      // Validate all required values are present
      if (!selectedFrame || !selectedSize || !selectedStyle || !imageUrl || !selectedSizeDetails) {
        throw new Error('Missing frame configuration. Please go back and reconfigure your frame.');
      }
      
      const frameConfig = {
        selectedFrame,
        selectedSize,
        selectedStyle,
        imageUrl,
        frameUrl: getFrameUrl(selectedFrame),
        frameName: selectedFrameDetails?.name || `${selectedFrame.charAt(0).toUpperCase() + selectedFrame.slice(1)} Wood`,
        sizeDetails: selectedSizeDetails,
        timestamp: Date.now()
      };
      
      console.log('Frame config being stored:', frameConfig);
      
      try {
        localStorage.setItem('frameConfig', JSON.stringify(frameConfig));
        
        // Verify it was stored correctly
        const storedConfig = localStorage.getItem('frameConfig');
        console.log('Verification - stored frame config:', storedConfig);
        
        if (!storedConfig) {
          throw new Error('Failed to store frame configuration');
        }
        
        const parsedConfig = JSON.parse(storedConfig);
        console.log('Verification - parsed stored config:', parsedConfig);
        
        if (parsedConfig.selectedSize !== selectedSize) {
          throw new Error(`Frame size mismatch: expected ${selectedSize}, got ${parsedConfig.selectedSize}`);
        }
        
        console.log('✅ Frame configuration stored and verified successfully');
      } catch (storageError) {
        console.error('❌ Error storing frame configuration:', storageError);
        console.warn('localStorage backup failed, but database save should be sufficient');
      }

      // Create Stripe checkout session with calculated shipping cost
      const successUrl = `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = window.location.href;
      
      console.log('=== CREATING STRIPE CHECKOUT SESSION ===');
      console.log('Product price:', productPrice);
      console.log('Frame name:', selectedFrameDetails?.name);
      console.log('Shipping cost being sent to Stripe:', shippingCost);
      
      // Track checkout button click for Google Analytics
      trackCheckoutButtonClick({
        total: discountedTotal,
        subtotal: productPrice * 0.80, // Discounted product price
        shipping: shippingCost,
        style: selectedStyle,
        frame: selectedFrame,
        size: selectedSize
      });
      
      trackFunnelStep('Checkout Button Clicked', 7, {
        order_value: discountedTotal,
        payment_method: 'stripe'
      });

      // Track order start in Reddit
      trackRedditOrderStart(discountedTotal, email.trim());

      // Track order start in Facebook
      trackFacebookOrderStart(discountedTotal, email.trim());

      // Track order start in Pinterest
      trackPinterestOrderStart(discountedTotal, email.trim());
      
      const { url } = await createFrameCheckoutSession(
        selectedSize as 'XS' | 'S' | 'M' | 'L',
        successUrl,
        cancelUrl,
        shippingMethod,
        shippingCost, // Pass the calculated shipping cost
        propertySearchId || undefined, // Convert null to undefined
        email.trim() // Pass customer email
      );

      // 🎯 TRACK CHECKOUT INITIATED EVENT
      console.log('🎯 Tracking checkout initiated event...');
      try {
        // Extract checkout session ID from the URL if possible
        const urlObj = new URL(url);
        const checkoutSessionId = urlObj.searchParams.get('session_id') || urlObj.pathname.split('/').pop();
        
        // Get campaign info if this is a campaign property
        let campaignInfo = null;
        if (propertySearchId) {
          try {
            // Get property token to use with bridge function
            const { data: propertyData, error } = await supabase
              .from('property_searches')
              .select('property_token')
              .eq('id', propertySearchId)
              .maybeSingle();
            
            if (!error && propertyData?.property_token) {
              const { propertyTokenGenerator } = await import('../services/tokenGenerator');
              const propertyResult = await propertyTokenGenerator.bridgeAccessCodeToPropertySearch(propertyData.property_token);
              if (propertyResult) {
                campaignInfo = await campaignTracking.getCampaignPropertyInfo(propertyResult.accessCode || propertyData.property_token);
              }
            }
          } catch (lookupError) {
            console.log('Could not find campaign info for checkout tracking:', propertySearchId);
          }
        }
        
        await campaignTracking.trackCheckoutInitiated({
          propertyId: propertySearchId || '',
          campaignId: campaignInfo?.campaignId,
          accessCode: campaignInfo?.accessCode,
          checkoutSessionId: checkoutSessionId || undefined
        });
        
        console.log('✅ Successfully tracked checkout initiated event', {
          propertyId: propertySearchId,
          campaignId: campaignInfo?.campaignId,
          checkoutSessionId: checkoutSessionId,
          isCampaignProperty: !!campaignInfo?.campaignId
        });
      } catch (trackingError) {
        console.warn('⚠️ Failed to track checkout initiated event:', trackingError);
        // Continue anyway - tracking shouldn't block the user experience
      }

      // Redirect to Stripe checkout
      if (url) {
        console.log('Redirecting to Stripe checkout:', url);
        
        // Track Stripe checkout access in widget with image URLs
        const artworkUrl = getImageUrl();
        const frameUrl = getFrameUrl(selectedFrame);
        
        window.trackConversion?.('accessed_stripe_checkout', {
          artworkUrl: artworkUrl,
          frameUrl: frameUrl,
          selectedStyle: selectedStyle,
          selectedFrame: selectedFrame,
          selectedSize: selectedSize,
          order_total: productPrice * 0.80 + (shippingCost || 0),
          product_price: productPrice * 0.80,
          shipping_cost: shippingCost || 0,
          style: selectedStyle,
          frame: selectedFrame,
          size: selectedSize,
          shipping_method: shippingMethod
        });
        
        // 🎯 TRACK PAYMENT PAGE ACCESS EVENT
        console.log('🎯 Tracking payment page access event...');
        try {
          // Extract checkout session ID from the URL if possible
          const urlObj = new URL(url);
          const checkoutSessionId = urlObj.searchParams.get('session_id') || urlObj.pathname.split('/').pop();
          
          // Get campaign info if this is a campaign property (reuse from checkout tracking above)
          let campaignInfo = null;
          if (propertySearchId) {
            try {
              // Get property token to use with bridge function
              const { data: propertyData, error } = await supabase
                .from('property_searches')
                .select('property_token')
                .eq('id', propertySearchId)
                .maybeSingle();
              
              if (!error && propertyData?.property_token) {
                const { propertyTokenGenerator } = await import('../services/tokenGenerator');
                const propertyResult = await propertyTokenGenerator.bridgeAccessCodeToPropertySearch(propertyData.property_token);
                if (propertyResult) {
                  campaignInfo = await campaignTracking.getCampaignPropertyInfo(propertyResult.accessCode || propertyData.property_token);
                }
              }
            } catch (lookupError) {
              console.log('Could not find campaign info for payment tracking:', propertySearchId);
            }
          }
          
          await campaignTracking.trackPaymentPageAccessed({
            propertyId: propertySearchId || '',
            campaignId: campaignInfo?.campaignId,
            accessCode: campaignInfo?.accessCode,
            checkoutSessionId: checkoutSessionId || undefined
          });
          
          console.log('✅ Successfully tracked payment page access event', {
            propertyId: propertySearchId,
            campaignId: campaignInfo?.campaignId,
            checkoutSessionId: checkoutSessionId,
            isCampaignProperty: !!campaignInfo?.campaignId
          });
        } catch (trackingError) {
          console.warn('⚠️ Failed to track payment page access event:', trackingError);
          // Continue anyway - tracking shouldn't block the user experience
        }
        
        // Send Stripe access notification
        if (imageSource && selectedStyle && selectedFrame && selectedSize) {
          await notifyStripeAccess(
            imageSource,
            selectedStyle,
            selectedFrame,
            selectedSize as 'XS' | 'S' | 'M' | 'L',
            totalPrice,
            imageSource === 'address' ? address : undefined
          );
        }
        
        window.location.href = url;
      } else {
        throw new Error('Failed to create checkout session');
      }

    } catch (error) {
      console.error('Error placing order:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
      setIsOptimizing(false);
    }
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4 overflow-hidden">
      <div className="bg-white rounded-xl max-w-xl w-full max-h-[90vh] flex flex-col relative">
        {/* Fixed header */}
        <div className="sticky top-0 z-50 bg-white p-4 border-b border-gray-200 rounded-t-xl flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {checkoutStep === 'details' ? 'Your Details' : 'Shipping'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Pinned Error Messages */}
        {error && (
          <div className="sticky top-[73px] z-40 bg-red-50 text-red-600 p-4 border-b border-red-200">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-600 text-xs font-medium">!</span>
              </div>
              <div className="text-sm">{error}</div>
            </div>
          </div>
        )}

        {success && (
          <div className="sticky top-[73px] z-40 bg-green-50 text-green-600 p-4 border-b border-green-200">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-green-600 text-xs font-medium">✓</span>
              </div>
              <div className="text-sm">{success}</div>
            </div>
          </div>
        )}

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Step 1: Your Details */}
          {checkoutStep === 'details' && (
            <div className="flex flex-col h-full">
              {/* Frame Preview */}
              <div className="flex-shrink-0 flex justify-center mb-4">
                <div className="w-64 h-56 md:w-80 md:h-[280px] relative">
                  {/* Selected Image Behind Frame */}
                  {getImageUrl() && (
                    <div className="absolute inset-[20%] overflow-hidden">
                      <img
                        src={getImageUrl()}
                        alt={`${selectedStyle} style`}
                        className="absolute inset-0 w-full h-full object-contain"
                      />
                    </div>
                  )}
                  {/* Frame Overlay */}
                  <img
                    src={getFrameUrl(selectedFrame)}
                    alt={`${selectedFrame} frame`}
                    className="relative w-full h-full object-contain"
                  />
                </div>
              </div>

              <form onSubmit={handleDetailsSubmit} className="flex-1 flex flex-col justify-between">
                <div className="space-y-5">
                  {/* Split Name Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        placeholder="Enter your first name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                {/* Continue Button */}
                <div className="mt-auto pt-6">
                  <button
                    type="submit"
                    className="w-full bg-black text-white py-3 rounded-md hover:bg-gray-800 transition-colors font-medium"
                  >
                    Continue to Shipping
                  </button>
                  <div className="text-center mt-3 text-xs text-gray-500">
                    Powered by Stripe
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Step 2: Shipping */}
          {checkoutStep === 'shipping' && (
            <>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            {/* Frame Image */}
            <div className="flex justify-start mb-4">
              <div className="w-28 h-24 relative" style={{ marginLeft: '-10px', marginTop: '-10px' }}>
                {/* Selected Image Behind Frame */}
                {getImageUrl() && (
                  <div className="absolute inset-[20%] overflow-hidden">
                    <img
                      src={getImageUrl()}
                      alt={`${selectedStyle} style`}
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  </div>
                )}
                {/* Frame Overlay */}
                <img
                  src={getFrameUrl(selectedFrame)}
                  alt={`${selectedFrame} frame`}
                  className="relative w-full h-full object-contain"
                />
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <div className="text-sm font-normal text-black">{selectedSizeDetails?.frameWidth || selectedSizeDetails?.width}" x {selectedSizeDetails?.frameHeight || selectedSizeDetails?.height}" FRAME</div>
                <div className="text-sm font-normal text-gray-600">{selectedFrameDetails?.name || `${selectedFrame.charAt(0).toUpperCase() + selectedFrame.slice(1)} Wood`}</div>
              </div>
              
              <div>
                <div className="text-sm font-normal text-black">{selectedSizeDetails?.width}" x {selectedSizeDetails?.height}" {selectedStyle.toUpperCase()}-STYLE PRINT</div>
                    <div className="text-sm font-normal text-gray-600">Fine art print on cold-pressed artist paper with {getMatSize(selectedSize)} mat</div>
              </div>
              
              <div className="mb-2">
                <div className="text-sm font-normal text-gray-600">Mounted & framed with acrylic glass, hanging wire</div>
              </div>
              
              {/* Price Section */}
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-normal text-black">Framed Artwork:</span>
                  <span className="text-sm font-normal text-black">${productPrice.toFixed(2)}</span>
                </div>
                
                {/* Discount on Product */}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-normal text-green-600">Limited Offer Discount (20%):</span>
                  <span className="text-sm font-normal text-green-600">
                    -${(productPrice * 0.20).toFixed(2)}
                  </span>
                </div>
                
                {/* Subtotal after discount */}
                <div className="flex justify-between items-center border-t border-gray-100 pt-2 mt-2">
                  <span className="text-sm font-normal text-gray-900">Discounted Price:</span>
                  <span className="text-sm font-normal text-gray-900">
                    ${(productPrice * 0.80).toFixed(2)}
                  </span>
                </div>
                
                {/* Shipping */}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-normal text-black">{shippingMethod === 'overnight' ? 'Express Shipping:' : 'Standard Shipping:'}</span>
                  <span className="text-sm font-normal text-black">
                    {shippingCost !== null ? (
                      `$${shippingCost.toFixed(2)}`
                    ) : (
                      <span className="text-blue-600 text-xs">Confirm address below</span>
                    )}
                  </span>
                </div>
                
                {/* Final Total */}
                <div className="flex justify-between items-center font-medium border-t border-gray-200 pt-2 mt-2">
                  <span className="text-sm text-black">Total:</span>
                  <div className="text-right">
                    {shippingCost !== null ? (
                      <>
                        <div className="text-xs text-gray-400 line-through">${totalPrice.toFixed(2)}</div>
                        <div className="text-sm text-black font-medium">${((productPrice * 0.80) + shippingCost).toFixed(2)}</div>
                      </>
                    ) : (
                      <div className="text-sm text-black font-medium">
                        ${(productPrice * 0.80).toFixed(2)}
                        <span className="text-xs text-gray-500 font-normal"> + shipping</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

              {/* Customer Details Summary */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium text-gray-900">Your Details</h3>
                  <button
                    type="button"
                    onClick={() => setCheckoutStep('details')}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  </div>
                <div className="text-sm text-gray-900 space-y-1">
                  <div><span className="font-medium">Name:</span> {firstName} {lastName}</div>
                  <div><span className="font-medium">Email:</span> <span className="text-gray-600">{email}</span></div>
                  </div>
                </div>
                
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Shipping Address Section */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-gray-900">Shipping Address</h3>
                    <button
                      type="button"
                      onClick={() => setIsEditingAddress(!isEditingAddress)}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                  </div>
                  
                  {isLoadingPropertyData ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      <span className="text-gray-600">Loading address...</span>
                    </div>
                  ) : isEditingAddress ? (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="shipping_addressLine1" className="block text-sm font-medium text-gray-700 mb-1">
                          Address Line 1 <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="shipping_addressLine1"
                          name="addressLine1"
                          type="text"
                          value={shippingAddress.addressLine1}
                          onChange={handleShippingChange}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isFieldInvalid(shippingAddress.addressLine1) 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300'
                          }`}
                          required
                          placeholder="Street number and name"
                        />
                      </div>

                      <div>
                        <label htmlFor="shipping_addressLine2" className="block text-sm font-medium text-gray-700 mb-1">
                          Address Line 2
                        </label>
                        <input
                          id="shipping_addressLine2"
                          name="addressLine2"
                          type="text"
                          value={shippingAddress.addressLine2}
                          onChange={handleShippingChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Apartment, suite, unit, etc. (optional)"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label htmlFor="shipping_city" className="block text-sm font-medium text-gray-700 mb-1">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="shipping_city"
                            name="city"
                            type="text"
                            value={shippingAddress.city}
                            onChange={handleShippingChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              isFieldInvalid(shippingAddress.city) 
                                ? 'border-red-300 bg-red-50' 
                                : 'border-gray-300'
                            }`}
                            required
                            placeholder="City"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="shipping_state" className="block text-sm font-medium text-gray-700 mb-1">
                            State <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="shipping_state"
                            name="state"
                            type="text"
                            value={shippingAddress.state}
                            onChange={handleShippingChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              isFieldInvalid(shippingAddress.state) 
                                ? 'border-red-300 bg-red-50' 
                                : 'border-gray-300'
                            }`}
                            required
                            placeholder="State"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="shipping_zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                            Zip Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="shipping_zipCode"
                            name="zipCode"
                            type="text"
                            value={shippingAddress.zipCode}
                            onChange={handleShippingChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              isFieldInvalid(shippingAddress.zipCode) 
                                ? 'border-red-300 bg-red-50' 
                                : 'border-gray-300'
                            }`}
                            required
                            placeholder="ZIP Code"
                          />
                        </div>
                      </div>

                      <div className="bg-gray-50 p-3 rounded-md">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700">Country:</span>
                          <span className="text-sm text-gray-900">United States</span>
                          <span className="text-xs text-gray-500">(Currently shipping to US only)</span>
                        </div>
                      </div>

                      {/* Confirm Address Button */}
                      {(pendingAddressChanges || !isAddressConfirmed) && isAddressComplete(shippingAddress) && (
                        <div className="pt-3 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={handleConfirmAddress}
                            className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Confirm Address
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="text-gray-700">
                        <div className="space-y-1">
                          <div>{shippingAddress.addressLine1}</div>
                          {shippingAddress.addressLine2 && <div>{shippingAddress.addressLine2}</div>}
                          <div>
                            {shippingAddress.city && `${shippingAddress.city}, `}
                            {shippingAddress.state} {shippingAddress.zipCode}
                          </div>
                          <div>{shippingAddress.country === 'US' ? 'United States' : shippingAddress.country}</div>
                        </div>
                        {isAddressConfirmed && (
                          <div className="flex items-center gap-2 mt-2 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm">Address confirmed</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Integrated Shipping Calculator */}
                      {isAddressConfirmed && isAddressComplete(shippingAddress) && (
                        <div className="border-t border-gray-200 mt-4 pt-3">
                          <ShippingCalculator
                            frameType={selectedFrame}
                            frameSize={selectedSize}
                            recipient={{
                              firstName: firstName,
                              lastName: lastName,
                              addressLine1: shippingAddress.addressLine1,
                              addressLine2: shippingAddress.addressLine2,
                              city: shippingAddress.city,
                              state: shippingAddress.state,
                              zipCode: shippingAddress.zipCode,
                              country: shippingAddress.country,
                              phone: '',
                              company: ''
                            }}
                            onShippingChange={handleShippingUpdate}
                            selectedMethod={shippingMethod}
                            isAddressConfirmed={isAddressConfirmed}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Address Confirmation Required Notice */}
                  {!isAddressConfirmed && isAddressComplete(shippingAddress) && !isEditingAddress && (
                    <div className="mt-4 p-3 bg-yellow-50 text-yellow-800 rounded-md">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm">Please confirm your shipping address to calculate shipping costs</span>
                      </div>
                      <button
                        type="button"
                        onClick={handleConfirmAddress}
                        className="mt-2 bg-yellow-600 text-white py-1 px-3 rounded text-sm hover:bg-yellow-700 transition-colors"
                      >
                        Confirm Address
                      </button>
                    </div>
                  )}

                  {/* Incomplete Address Notice */}
                  {!isAddressComplete(shippingAddress) && !isEditingAddress && (
                    <div className="mt-4 p-3 bg-red-50 text-red-800 rounded-md">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm">Please complete your shipping address</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsEditingAddress(true)}
                        className="mt-2 bg-red-600 text-white py-1 px-3 rounded text-sm hover:bg-red-700 transition-colors"
                      >
                        Edit Address
                      </button>
                    </div>
                  )}
              </div>
              
              <div className="flex items-center">
                <input
                  id="useSameForBilling"
                  type="checkbox"
                  checked={useSameForBilling}
                  onChange={handleUseSameForBillingChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="useSameForBilling" className="ml-2 block text-sm text-gray-700">
                  Use shipping address as billing address
                </label>
              </div>
              
              {/* Billing Address Section - only shown when checkbox is unchecked */}
              {showBillingFields && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-gray-900">Billing Address</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="billing_addressLine1" className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 1 <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="billing_addressLine1"
                        name="addressLine1"
                        type="text"
                        value={billingAddress.addressLine1}
                        onChange={handleBillingChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !useSameForBilling && isFieldInvalid(billingAddress.addressLine1) 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300'
                        }`}
                        required={!useSameForBilling}
                        placeholder="Street number and name"
                      />
                    </div>

                    <div>
                      <label htmlFor="billing_addressLine2" className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 2
                      </label>
                      <input
                        id="billing_addressLine2"
                        name="addressLine2"
                        type="text"
                        value={billingAddress.addressLine2}
                        onChange={handleBillingChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Apartment, suite, unit, etc. (optional)"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="billing_city" className="block text-sm font-medium text-gray-700 mb-1">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="billing_city"
                          name="city"
                          type="text"
                          value={billingAddress.city}
                          onChange={handleBillingChange}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            !useSameForBilling && isFieldInvalid(billingAddress.city) 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300'
                          }`}
                          required={!useSameForBilling}
                          placeholder="City"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="billing_state" className="block text-sm font-medium text-gray-700 mb-1">
                          State <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="billing_state"
                          name="state"
                          type="text"
                          value={billingAddress.state}
                          onChange={handleBillingChange}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            !useSameForBilling && isFieldInvalid(billingAddress.state) 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300'
                          }`}
                          required={!useSameForBilling}
                          placeholder="State"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="billing_zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                          Zip Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="billing_zipCode"
                          name="zipCode"
                          type="text"
                          value={billingAddress.zipCode}
                          onChange={handleBillingChange}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            !useSameForBilling && isFieldInvalid(billingAddress.zipCode) 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300'
                          }`}
                          required={!useSameForBilling}
                          placeholder="ZIP Code"
                        />
                      </div>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-md">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Country:</span>
                        <span className="text-sm text-gray-900">United States</span>
                        <span className="text-xs text-gray-500">(Currently billing to US only)</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Checkout button */}
              <div className="pt-5">
          <button
                  type="submit"
            disabled={isSubmitting || isOptimizing || !isAddressConfirmed || !shippingCalculated}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
              isSubmitting || isOptimizing || !isAddressConfirmed || !shippingCalculated
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-black text-white hover:bg-gray-800'
            }`}
          >
            {isOptimizing ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Loading Checkout Page...
              </span>
            ) : isSubmitting ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Loading Checkout Page...
              </span>
            ) : !isAddressConfirmed ? (
                    'Confirm Address to Continue'
            ) : !shippingCalculated || shippingCost === null ? (
                    'Calculating Shipping...'
            ) : (
              `Make Payment $${((productPrice * 0.80) + (shippingCost || 0)).toFixed(2)}`
            )}
          </button>
                <div className="text-center mt-3 text-xs text-gray-500 pb-4">
                  Powered by Stripe
                </div>
              </div>
          </form>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default OrderForm;